import { createContext, useCallback, useContext, useMemo, useState, type ReactElement } from 'react'
import { isDefined, type TeamData } from '@terros/common'
import { useTeamHierarchyContext } from './TeamHierarchyContext'

type DraggedTeam = TeamData & {
  highestChildLevel?: number
}

type DragState = {
  draggedTeam: DraggedTeam | null
  dragOverTeam: TeamData | null
}

type TeamDragContextType = {
  dragState: DragState
  startDrag: (team: TeamData, highestChildLevel?: number) => void
  endDrag: () => void
  setDragOverTeam: (team: TeamData | null) => void
  canDrop: (targetTeam: TeamData) => boolean
}

const TeamDragContext = createContext<TeamDragContextType | null>(null)

export const TeamDragProvider = ({ children }: { children: ReactElement }): ReactElement => {
  const { company } = useTeamHierarchyContext()
  const [dragState, setDragState] = useState<DragState>({
    draggedTeam: null,
    dragOverTeam: null,
  })

  const startDrag = useCallback((team: TeamData, highestChildLevel?: number): void => {
    setDragState((prev) => ({ ...prev, draggedTeam: { ...team, highestChildLevel } }))
  }, [])

  const endDrag = useCallback((): void => {
    setDragState({
      draggedTeam: null,
      dragOverTeam: null,
    })
  }, [])

  const setDragOverTeam = useCallback((team: TeamData | null): void => {
    setDragState((prev) => ({ ...prev, dragOverTeam: team }))
  }, [])

  const { draggedTeam } = dragState

  const canDrop = useCallback(
    (targetTeam: TeamData): boolean => {
      if (!draggedTeam) return false
      // Can't drop into itself
      if (targetTeam.teamId === draggedTeam.teamId) return false
      // Can't drop into its direct parent
      if (targetTeam.teamId === draggedTeam.parentTeamIds?.[0]) return false
      // Can't drop into one of its children
      if (targetTeam.parentTeamIds.includes(draggedTeam.teamId)) return false
      // can't make dragged team same level as its highest child
      if (isDefined(draggedTeam.highestChildLevel) && targetTeam.level - 1 <= draggedTeam.highestChildLevel)
        return false
      // Can't drop into the lowest level team
      if (targetTeam.level === company.lowestTeamLevel) return false
      return true
    },
    [company.lowestTeamLevel, draggedTeam]
  )

  const contextValue = useMemo(
    () => ({ dragState, startDrag, endDrag, setDragOverTeam, canDrop }),
    [dragState, startDrag, endDrag, setDragOverTeam, canDrop]
  )

  return <TeamDragContext.Provider value={contextValue}>{children}</TeamDragContext.Provider>
}

export const useTeamDrag = (): TeamDragContextType => {
  const context = useContext(TeamDragContext)
  if (!context) {
    throw new Error('useTeamDrag must be used within a TeamDragProvider')
  }
  return context
}
